<template>
  <div class="chart-card" :class="{ 'chart-card-loading': loading }">
    <div class="chart-card-header">
      <div class="chart-card-heading">
        <h4>{{ title }}</h4>
        <p v-if="subtitle" class="chart-card-subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-card-toggle" role="tablist">
        <span class="chart-card-toggle-thumb" :class="{ 'is-table': mode === 'table' }"></span>
        <button role="tab" :aria-selected="mode === 'chart'" :class="{ active: mode === 'chart' }" @click="mode = 'chart'">
          Chart
        </button>
        <button role="tab" :aria-selected="mode === 'table'" :class="{ active: mode === 'table' }" @click="mode = 'table'">
          Table
        </button>
      </div>
    </div>

    <!-- First load: no previous render to hold, so show the spinner.
         Refetch with data already on screen: keep the old render dimmed
         (chart-card-loading) instead of flashing back to a spinner. -->
    <div v-if="loading && !hasData" class="chart-card-spinner">
      <i class="pi pi-spin pi-spinner"></i>
    </div>

    <div v-else-if="!hasData" class="chart-card-empty">No data for this period.</div>

    <apexchart
      v-else-if="mode === 'chart'"
      :type="type"
      :height="height"
      :options="mergedOptions"
      :series="series"
    />

    <AnalyticsDataTable v-else :categories="categories" :series="tableSeries" />
  </div>
</template>

<script>
import AnalyticsDataTable from '@/components/analytics/DataTable.vue';

/**
 * One card per panel: title, optional unit subtitle, Chart/Table toggle,
 * empty state.
 *
 * `subtitle` exists so units stop riding in the title as a parenthetical -
 * "Register variance" with "units per day" beneath it reads as a heading,
 * "Register variance (units/day)" reads as a column name.
 *
 * The Table toggle is load-bearing, not decoration - see chartTheme.js on the
 * sub-3:1 contrast series. On refetch the card holds its previous render at
 * reduced opacity (`chart-card-loading`) rather than flashing a skeleton.
 */
export default {
  name: 'ChartCard',
  components: { AnalyticsDataTable },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    type: { type: String, default: 'bar' },
    height: { type: [Number, String], default: 280 },
    options: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    series: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { mode: 'chart' };
  },
  computed: {
    hasData() {
      return this.series && this.series.length > 0;
    },
    mergedOptions() {
      // Categories ride on options for apex; keeping them a separate prop
      // means every view passes data one way and the table gets them free.
      return {
        ...this.options,
        xaxis: { ...(this.options.xaxis || {}), categories: this.categories },
      };
    },
    tableSeries() {
      // Points can arrive object-shaped ({ x, y }); flatten anything
      // non-numeric so the toggle never renders [object Object].
      return this.series.map((s) => ({
        name: s.name,
        data: (s.data || []).map((d) =>
          d !== null && typeof d === 'object' ? (Array.isArray(d.y) ? `${d.x}: ${d.y.join(' → ')}` : d.x) : d
        ),
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-card {
  background: white;
  border: 1px solid rgba(11, 11, 11, 0.07);
  border-radius: 10px;
  padding: 16px 18px 8px 18px;
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: opacity 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 14px rgba(11, 11, 11, 0.08);
    transform: translateY(-1px);
  }
}
.chart-card-loading {
  opacity: 0.45;
  pointer-events: none;
}
.chart-card-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;

  i {
    font-size: 1.6rem;
    color: #7a7f85;
  }
}
.chart-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.chart-card-heading {
  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.1px;
    color: #333333;
    text-align: left;
  }
}
.chart-card-subtitle {
  margin: 2px 0 0 0;
  font-size: 11px;
  color: #7a7f85;
  text-align: left;
}
.chart-card-toggle {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  padding: 2px;
  border-radius: 999px;
  background: #f2f4f6;
  font-size: 11px;

  button {
    position: relative;
    z-index: 1;
    min-width: 46px;
    padding: 3px 10px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    font-family: inherit;
    font-size: 11px;
    color: #7a7f85;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #333333;
    }
    &.active {
      color: white;
      font-weight: 600;
    }
  }
}
.chart-card-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  border-radius: 999px;
  background: #2a78d6;
  transition: transform 0.2s ease;

  &.is-table {
    transform: translateX(100%);
  }
}
.chart-card-empty {
  padding: 40px 0;
  font-size: 13px;
  color: #7a7f85;
}
</style>
